/* eslint-disable react/no-unstable-nested-components */
// import { SplitText } from '@cyriacbr/react-split-text';
import { useScreenSize } from '@hooks';
import { bool, func, number, shape, string } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSpring } from 'react-spring';

import TapToSeeButton from '../TapToSeeButton/TapToSeeButton';
import {
  Back,
  CardContainer,
  CardContainer2,
  DURATION,
  FLIP_DURATION,
  Front,
  getCardHeight,
  styles,
} from './AnimatedCardsFrame.layoutProps';

const { Button, Flex, Typography, theme } = require('@styles');

const CardDefinitionWithAnimation = ({
  data,
  direction,
  isActive,
  isBackDirection,
  isLastSlide,
  isSkipped,
  onFinish,
  setButtonsBlocked,
  zIndex,
}) => {
  const { i18n } = useTranslation();
  const { dynamicWidth, isLaptop, isTabletLG } = useScreenSize();

  const [rotateFront, setRotateFront] = useState(false);
  const [isAnimatingFlip, setIsAnimatingFlip] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  const coordinates = useMemo(
    () => ({
      from:
        isBackDirection && isSkipped
          ? {
              rotate: isLastSlide ? 0 : direction * 50,
              x: isLastSlide ? 0 : direction * window.innerWidth * 1.3,
              y: 0,
            }
          : { rotate: 0, x: 0, y: 0 },
      to: isBackDirection
        ? { rotate: 0, x: 0, y: 0 }
        : {
            rotate: direction * 50,
            x: direction * window.innerWidth * 1.3,
            y: 0,
          },
    }),
    [isBackDirection, isLastSlide]
  );

  const animationProps = useSpring({
    config: { delay: DURATION, friction: 40, mass: 5, tension: DURATION },
    from: coordinates.from,
    to: async (next) => {
      if (isAnimatingOut) {
        await next(coordinates.to);
      }
    },
  });

  useEffect(() => {
    if (isBackDirection && isActive) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setIsAnimatingOut(false);
        setButtonsBlocked(false);
      }, DURATION);
    } else if (isSkipped) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setIsAnimatingOut(false);
        setButtonsBlocked(false);
      }, DURATION);
    }
  }, [isActive, isSkipped]);

  const onFllipAnimation = () => {
    setIsAnimatingFlip(true);
    setButtonsBlocked(true);
    setRotateFront((prev) => !prev);
    setTimeout(() => {
      setIsAnimatingFlip(false);
      setButtonsBlocked(false);
    }, FLIP_DURATION);
  };

  return (
    <CardContainer2
      dynamicWidth={dynamicWidth}
      isActive={isActive || isAnimatingOut}
      style={animationProps}
      zIndex={zIndex}
    >
      {onFinish && isLastSlide && (
        <Button
          borderRadius={0}
          bottom={0}
          onClick={onFinish}
          position="absolute"
          px={47}
          py={22}
          right={0}
          variant="primary"
          zIndex={50}
        >
          <Typography
            color={theme.colors.background}
            variant="MS-semibold-s16-lh25"
          >
            {i18n.t('Activities.Continue').toUpperCase()}
          </Typography>
        </Button>
      )}
      <CardContainer
        dynamicWidth={dynamicWidth}
        isAnimatingFlip={isAnimatingFlip}
        onClick={onFllipAnimation}
      >
        <Front rotateFront={rotateFront}>
          <TapToSeeButton position="absolute" right={10} top={10} />
          <Flex
            backgroundColor={theme.colors.background}
            flexDirection="column"
            height={getCardHeight({ dynamicWidth })}
            px={isLaptop ? 24 : 80}
            py={40}
            width="100%"
          >
            {data?.topText && (
              <Typography
                color={theme.colors.white[100]}
                opacity="0.6"
                variant={
                  isTabletLG ? 'MS-regular-s18-lh22' : 'MS-regular-s24-lh37'
                }
              >
                {data?.topText}
              </Typography>
            )}
            <Typography
              alignItems="center"
              color={theme.colors.white[100]}
              display="flex"
              height="100%"
              justifyContent="center"
              textAlign="center"
              variant="BN-bold-s43-lh38"
            >
              {data?.statement}
            </Typography>
          </Flex>
        </Front>
        <Back rotateFront={rotateFront}>
          <Flex
            backgroundColor={theme.colors.background}
            flexDirection="column"
            height={getCardHeight({ dynamicWidth })}
            px={dynamicWidth < 1250 ? 24 : 80}
            py={40}
            width="100%"
          >
            {data?.topText && (
              <Typography
                color={theme.colors.white[100]}
                opacity="0.6"
                variant={
                  isTabletLG ? 'MS-regular-s18-lh22' : 'MS-regular-s24-lh37'
                }
              >
                {data?.topText}
              </Typography>
            )}
            <Flex alignItems="center" height="100%" justifyContent="center">
              <Typography
                {...styles.cardDefinition}
                variant={
                  isTabletLG ? 'MS-regular-s18-lh22' : 'MS-regular-s20-lh31'
                }
              >
                {data?.definition}
              </Typography>
            </Flex>
          </Flex>
        </Back>
      </CardContainer>
    </CardContainer2>
  );
};

CardDefinitionWithAnimation.propTypes = {
  data: shape({
    definition: string.isRequired,
    statement: string.isRequired,
    topText: string,
  }).isRequired,
  direction: number.isRequired,
  isActive: bool,
  isBackDirection: bool,
  isLastSlide: bool,
  isSkipped: bool,
  onFinish: func,
  setButtonsBlocked: func.isRequired,
  zIndex: number.isRequired,
};

CardDefinitionWithAnimation.defaultProps = {
  isActive: false,
  isBackDirection: false,
  isLastSlide: false,
  isSkipped: false,
  onFinish: null,
};

export default CardDefinitionWithAnimation;
